import { useMemo, useState, SyntheticEvent } from 'react'

import { useTranslation } from 'next-i18next'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'

import { Result, Type } from '~/components/Search/types'

import { TheoryType } from '~/types/theory'

import { Breakpoint, useCheckBreakpoint } from '~/utils'

import { SearchResCard } from './SearchResCard'

function showResultInTab(tab: Type, type: Type & TheoryType): boolean {
  switch (tab) {
    case Type.SOFTWARE:
      return type === TheoryType.EXCEL || type === TheoryType.GEOGEBRA
    case Type.ALL:
      return true
    case Type.ENCYCLOPEDIA:
      return type === TheoryType.ENCYCLOPEDIA
    case Type.DRILL:
      return type === TheoryType.DRILL
    case Type.BOOTCAMP:
      return type === Type.BOOTCAMP
  }
}

export const SearchCategories = ({
  results,
  isModal,
}: {
  results: Result[]
  isModal?: boolean
}): React.ReactElement => {
  const { t } = useTranslation('search')
  const [activeTab, setActiveTab] = useState<Type>(Type.ALL)
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.SM })

  const handleOnChange = (event: SyntheticEvent, value: Type): void => {
    setActiveTab(value)
  }

  const filteredResults = useMemo(() => {
    return results.filter(({ type }) => showResultInTab(activeTab, type)) || []
  }, [results, activeTab])

  const handleTabNumber = (tab: Type): React.ReactElement => {
    const length = results.filter(({ type }) => showResultInTab(tab, type)).length
    return (
      <div className="flex relative">
        <div>{t(tab)}</div>
        <div className="transform -translate-y-3 -translate-x-2">
          <div className="bg-lightGreen text-black text-xxxs rounded-full h-4 w-4 flex items-center justify-center">
            {length}
          </div>
        </div>
      </div>
    )
  }

  return (
    <section className={`mx-auto ${isModal ? 'text-purple' : 'max-w-4xl text-white'}`}>
      <div className="px-2 mb-2">
        <Tabs
          aria-label="disabled tabs example"
          indicatorColor="primary"
          onChange={handleOnChange}
          sx={{
            '.MuiTabs-indicator': {
              backgroundColor: '#ffffff',
            },
          }}
          textColor="inherit"
          value={activeTab}
          variant="scrollable"
        >
          {Object.values(Type).map((tab) => (
            <Tab
              key={tab}
              label={handleTabNumber(tab)}
              sx={{
                textTransform: 'none',
                fontFamily: 'Poppins',
                fontWeight: 600,
                minWidth: isMobile ? '72px' : '160px',
                padding: '6px 12px',
                lineHeight: '24.5px',
                opacity: '.7',
                color: isModal ? '#000000' : '#ffffff',
                '&.Mui-selected': {
                  color: isModal ? '#000000' : '#ffffff',
                },
                '&:focus': {
                  color: isModal ? '#000000' : '#ffffff',
                },
                '&:hover': {
                  color: isModal ? '#000000' : '#ffffff',
                  opacity: 1,
                },
              }}
              value={tab}
            />
          ))}
        </Tabs>
      </div>
      <SearchResCard isModal={isModal} results={filteredResults} />
    </section>
  )
}
